import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel";
import { floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import i18n from "i18next";
import { concatWithPipe } from "src/utils/strings.tsx";
import { SalesPartContentLinesApi } from "src/api/generated/erp/sales/masterData/api/salesPartContentLinesApi.ts";
import { SalesPartContentLineForm } from "src/components/views/erp/sales/masterData/salesParts/SalesPartContentLineForm.tsx";
import { SalesPartPackageLineView } from "src/api/generated/erp/db/types/tables/salesPartPackageLineView.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesPartContentLinesDataGridProps {
	parentSalesPartId: number;
}

export const SalesPartContentLinesDataGrid = ({ parentSalesPartId }: SalesPartContentLinesDataGridProps) => {
	return (
		<ClientSideDataGridModel
			gridId={"DC3C7EBFBF31A8209"}
			fetchData={() => SalesPartContentLinesApi.getContentLines({ parentSalesPartId: parentSalesPartId })}
			initialParams={{}}
			getRows={(data) => data}
			getRowId={(row) => row.salesPartPackageLineId}
			render={({ dataGridProps }) => {
				return (
					<FormCrudDataGrid<SalesPartPackageLineView>
						columns={[
							integerColumn({
								field: "lineNumber",
								headerName: i18n.t("line_number"),
							}),
							textColumn({
								field: "salesPart" as any,
								headerName: i18n.t("sales_part"),
								valueGetter: (_, row) =>
									concatWithPipe(row.childSalesPartNo, row.childSalesPartDescription),
								width: 300,
							}),
							floatColumn({
								field: "quantity",
								headerName: i18n.t("quantity"),
							}),
							textColumn({
								field: "childSalesUnit",
								headerName: i18n.t("unit"),
							}),
						]}
						form={{
							addRowEnabled: true,
							editEnabled: true,
							dialogSize: "sm",
							dialogTitle: i18n.t("content_line"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.sales.masterData.salesPartContentLine.formDialog"),
								data: {
									parentSalesPartId,
									salesPartPackageLineId: row?.salesPartPackageLineId ?? null,
									childSalesPartId: row?.childSalesPartId ?? null,
								},
								pathSegments:
									row?.salesPartPackageLineId ?
										[`${i18n.t("content_line")} ${row.salesPartPackageLineId}`]
									:	[i18n.t("content_line")],
							}),
							component: ({ row, onCompleted, onFormEdited }) => (
								<SalesPartContentLineForm
									childSalesPartPackageLineId={row?.salesPartPackageLineId}
									parentSalesPartId={parentSalesPartId}
									onCompleted={onCompleted}
									onFormEdited={onFormEdited}
								/>
							),
						}}
						remove={async ({ items }) =>
							SalesPartContentLinesApi.delete_({
								salesPartPackageLineIds: items.map((item) => item.salesPartPackageLineId),
							})
						}
						agentContext={{
							key: defineAgentUiContextKey("erp.sales.masterData.salesPartContentLines.grid"),
							data: { parentSalesPartId },
							selectedRowPathSegment: (row) => `${i18n.t("content_line")} ${row.salesPartPackageLineId}`,
							rowFields: [
								"salesPartPackageLineId",
								"parentSalesPartId",
								"childSalesPartId",
								"childSalesPartNo",
							],
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
